// FloatingTimer
//
// Pinned to the bottom-right of every page when a timer is running. Shows
// elapsed time, the matter the timer is attached to, and quick actions:
//
//   * Stop & save     — opens the time-entry modal pre-filled
//   * Pause (cancel)  — drops the timer without saving
//
// Reads from the global TimerContext. Renders nothing when no timer is
// running, so it has zero visual cost outside of actual billing sessions.

import React, { useState, useEffect } from 'react';
import { Play, Square, X, Clock, Briefcase } from 'lucide-react';
import { useTimer } from '../contexts/TimerContext';
import { useFirm } from '../contexts/FirmContext';
import { useRoute } from '../lib/router';
import { listMatters, createTimeEntry } from '../lib/db';
import { formatDuration } from '../lib/formatters';
import toast from '../lib/toast';

function pad(n) { return n < 10 ? `0${n}` : `${n}`; }
function hms(ms) {
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

export default function FloatingTimer() {
  const timer = useTimer();
  const { firmId } = useFirm();
  const [, navigate] = useRoute();
  const [matters, setMatters] = useState([]);
  const [showSave, setShowSave] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [note, setNote]         = useState('');
  const [pickedMatter, setPickedMatter] = useState('');

  // Lazy-load matters once when the user clicks "Stop & save".
  useEffect(() => {
    if (!showSave || !firmId || matters.length > 0) return;
    listMatters(firmId).then(({ data }) => setMatters(data || []));
  }, [showSave, firmId, matters.length]);

  if (!timer.running) return null;

  const openSave = () => {
    setPickedMatter(timer.matterId || '');
    setNote(timer.description || '');
    setShowSave(true);
  };

  const closeSave = () => setShowSave(false);

  const handleSave = async (e) => {
    e?.preventDefault?.();
    if (!firmId) return;
    if (!pickedMatter) { toast.error('Pick a matter to log against.'); return; }
    setSaving(true);
    const payload = {
      matter_id:        pickedMatter,
      description:      note || null,
      duration_minutes: timer.billableMinutes,
      billable:         true,
      entry_date:       new Date().toISOString().slice(0, 10),
    };
    const { error } = await createTimeEntry(firmId, payload);
    setSaving(false);
    if (error) { toast.error('Could not save: ' + error.message); return; }
    toast.success(`Logged ${formatDuration(timer.billableMinutes)} to the matter.`);
    timer.resetTimer();
    setShowSave(false);
    setNote('');
  };

  const handleCancel = () => {
    if (!window.confirm('Discard this timer without saving? Your elapsed time will be lost.')) return;
    timer.resetTimer();
  };

  // ── Pill (collapsed when not saving) ───────────────────────────────────
  if (!showSave) {
    return (
      <div
        className="fixed bottom-4 right-4 z-40 flex items-center gap-2 px-3 py-2 rounded-full bg-slate-900 text-white shadow-2xl border border-slate-700"
        role="status"
        aria-label="Time tracker running"
      >
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
        </span>
        <Clock size={14} className="text-emerald-300" />
        <span className="font-mono text-sm tabular-nums tracking-wider">
          {hms(timer.elapsedMs)}
        </span>
        <button
          onClick={openSave}
          className="ml-1 px-2.5 py-1 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold transition"
        >
          <Square size={10} className="inline -mt-0.5 mr-1" />
          Stop
        </button>
        <button
          onClick={handleCancel}
          className="p-1 rounded-full text-slate-400 hover:text-white hover:bg-slate-700 transition"
          title="Discard timer"
          aria-label="Discard timer"
        >
          <X size={12} />
        </button>
      </div>
    );
  }

  // ── Save-form expanded card ────────────────────────────────────────────
  return (
    <div className="fixed bottom-4 right-4 z-40 w-80 max-w-[calc(100vw-2rem)] rounded-xl bg-white dark:bg-slate-900 shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
      <div className="px-4 py-3 bg-slate-900 text-white flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock size={14} className="text-emerald-300" />
          <span className="font-mono text-sm tabular-nums">{hms(timer.elapsedMs)}</span>
          <span className="text-xs text-slate-400">→ {formatDuration(timer.billableMinutes)}</span>
        </div>
        <button
          onClick={closeSave}
          className="p-1 rounded text-slate-300 hover:text-white hover:bg-slate-700 transition"
          aria-label="Close"
        >
          <X size={14} />
        </button>
      </div>

      <form onSubmit={handleSave} className="p-4 space-y-3">
        <div>
          <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Matter</label>
          <select
            value={pickedMatter}
            onChange={(e) => setPickedMatter(e.target.value)}
            className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 focus:ring-2 focus:ring-indigo-500 outline-none"
          >
            <option value="">Select a matter…</option>
            {matters.map((m) => (
              <option key={m.id} value={m.id}>
                {m.title} — {m.matter_number}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Description</label>
          <textarea
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="What did you work on?"
            className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 focus:ring-2 focus:ring-indigo-500 outline-none"
          />
        </div>
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={handleCancel}
            className="text-xs text-slate-500 hover:text-red-600 transition"
          >
            Discard
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => { closeSave(); navigate('/time'); }}
              className="px-3 py-1.5 text-xs rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200"
            >
              More options
            </button>
            <button
              type="submit"
              disabled={saving || !pickedMatter}
              className="px-3 py-1.5 text-xs rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save time entry'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

// Helper for any page that wants its own "Start timer" button. Returns
// the timer's start function so callers can pass {matterId, description}.
export { useTimer as useGlobalTimer };

// Reusable "Start timer" button for matter detail pages. Default
// styling matches a small primary button.
export function StartTimerButton({ matterId, description = '', className = '' }) {
  const { running, startTimer } = useTimer();
  if (running) return null;
  return (
    <button
      type="button"
      onClick={() => startTimer({ matterId, description })}
      className={[
        'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium transition',
        className,
      ].join(' ')}
    >
      <Play size={12} /> Start timer
      <Briefcase size={11} className="opacity-60" />
    </button>
  );
}
